import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Send, Eye, Clock, Mail, MessageCircle, X, Copy } from 'lucide-react';
import { useMembershipStatus, MembershipBlockedOverlay } from '../components/MembershipGuard';
import { PageSpinner } from '../components/PageSpinner';
import { Pagination } from '../components/Pagination';

const ITEMS_PER_PAGE = 15;

export default function Pendientes() {
  const { API_URL, company } = useContext(AppContext);
  const { status: membershipData, isBlocked, refetchMembership } = useMembershipStatus();
  const codigoFirmaEnabled = membershipData?.codigo_firma_habilitado || false;
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchPendientes();
  }, []);

  const fetchPendientes = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.get(`${API_URL}/consents/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendientes(response.data);
    } catch (error) {
      toast.error('Error al cargar consentimientos pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este consentimiento?')) return;

    try {
      const token = localStorage.getItem('konemed_token');
      await axios.delete(`${API_URL}/consents/pending/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Consentimiento eliminado');
      fetchPendientes();
      // Deleting a pending request restores one consentimiento to the plan's count.
      refetchMembership(true);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleResend = async (requestId, via) => {
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.post(
        `${API_URL}/consents/send-link`,
        { request_id: requestId, send_via: via },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (via === 'whatsapp' && response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Error al reenviar');
    }
  };

  const handleView = async (request) => {
    try {
      const token = localStorage.getItem('konemed_token');
      // Get template and company data
      const templateRes = await axios.get(`${API_URL}/consents/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const template = templateRes.data.find(t => t.id === request.template_id);
      
      const companyRes = await axios.get(`${API_URL}/company/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setViewData({
        request,
        template,
        company: companyRes.data
      });
      setViewModal(true);
    } catch (error) {
      toast.error('Error al cargar datos del consentimiento');
    }
  };

  return (
    <DashboardLayout>
      <div data-testid="pendientes-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">Consentimientos Pendientes</h1>
          <p className="text-slate-600">Consentimientos enviados que aún no han sido firmados</p>
        </div>

        {isBlocked && <MembershipBlockedOverlay mode="banner" />}

        {loading ? (
          <PageSpinner />
        ) : pendientes.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="empty-state">
            <Clock className="mx-auto text-slate-300 mb-4" size={64} />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No hay consentimientos pendientes</h3>
            <p className="text-slate-600">Todos los consentimientos han sido firmados o aún no has creado ninguno</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="pendientes-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Paciente</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Documento</th>
                    {codigoFirmaEnabled && (
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Código de Firma</th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Días Restantes</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Enviado Vía</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pendientes.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                        {item.nombre} {item.primer_apellido} {item.segundo_apellido}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 font-mono">
                        {item.numero_documento}
                      </td>
                      {codigoFirmaEnabled && (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-violet-600 bg-violet-50 px-2 py-1 rounded text-sm tracking-wider">
                              {item.codigo_firma || 'N/A'}
                            </span>
                            {item.codigo_firma && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(item.codigo_firma);
                                  toast.success('Código copiado');
                                }}
                                className="p-1 text-slate-400 hover:text-violet-600 transition-colors"
                                title="Copiar código"
                                data-testid={`copy-code-${item.id}`}
                              >
                                <Copy size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          item.dias_restantes <= 2
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : item.dias_restantes <= 5
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        }`}>
                          {item.dias_restantes} días
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 capitalize">
                        {item.sent_via || 'No enviado'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleView(item)}
                            data-testid={`view-button-${item.id}`}
                            className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                            title="Ver Consentimiento"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleResend(item.id, 'email')}
                            data-testid={`resend-email-button-${item.id}`}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Reenviar por Email"
                          >
                            <Mail size={18} />
                          </button>
                          <button
                            onClick={() => handleResend(item.id, 'whatsapp')}
                            data-testid={`resend-whatsapp-button-${item.id}`}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Reenviar por WhatsApp"
                          >
                            <MessageCircle size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            data-testid={`delete-button-${item.id}`}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4">
              <Pagination currentPage={currentPage} totalPages={Math.ceil(pendientes.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewModal && viewData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="view-modal">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-slate-900">Vista Previa del Consentimiento</h2>
                <button
                  onClick={() => setViewModal(false)}
                  data-testid="close-view-modal"
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-8">
                <div className="mb-6 pb-6 border-b border-slate-200">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Datos del Paciente</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Nombre:</span>
                      <p className="font-semibold">{viewData.request.nombre} {viewData.request.primer_apellido} {viewData.request.segundo_apellido}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Documento:</span>
                      <p className="font-semibold">{viewData.request.tipo_documento}: {viewData.request.numero_documento}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">Email:</span>
                      <p className="font-semibold">{viewData.request.email}</p>
                    </div>
                    <div>
                      <span className="text-slate-600">WhatsApp:</span>
                      <p className="font-semibold">+{viewData.request.codigo_pais} {viewData.request.numero_whatsapp}</p>
                    </div>
                  </div>
                </div>

                {viewData.template && (
                  <>
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{viewData.template.nombre_procedimiento}</h3>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-bold text-slate-900 mb-2">Datos del Centro:</h4>
                      <p className="text-sm text-slate-700"><strong>Razón Social:</strong> {viewData.company.razon_social}</p>
                      <p className="text-sm text-slate-700"><strong>Documento:</strong> {viewData.company.numero_documento}</p>
                      <p className="text-sm text-slate-700"><strong>Dirección:</strong> {viewData.company.direccion}</p>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-bold text-slate-900 mb-2">Políticas del Centro:</h4>
                      <p className="text-sm text-slate-700 text-justify whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                        {viewData.template.politicas_centro}
                      </p>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-bold text-slate-900 mb-2">Consentimiento del Procedimiento:</h4>
                      <p className="text-sm text-slate-700 text-justify whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                        {viewData.template.politicas_procedimiento}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
